import React, { PropsWithChildren, useMemo, useCallback, useState } from 'react';
import { Pressable, PressableProps, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import {
  DNABox,
  DNABoxProps,
  DNAButton,
  DNACard,
  DNACheckbox,
  DNAIcon,
  DNAText,
  DNATextProps,
  Iffy,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, useCurrentPage } from 'src/components/DNA';
import { VIDEO_TYPES_ENUM } from 'src/types/enums';
import { DocumentORM } from 'src/types/orms';
import { useDNALibrary } from 'src/screens/MyLibrary/DNALibrary';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useCurrentUserORM, useUserTenant } from 'src/state/redux/selector/user';
import CollapsibleCard, { CollapsibleCardProps } from '../CollapsibleCard/CollapsibleCard';
import {
  countDocumentCustomValues,
  DocumentCount,
  FilterMap,
  useDNADocumentFilters,
} from '../DNA/Document/DNADocumentFilters';
import { sortAiCategoryEntries, sortAiFilterValueEntries } from './aiFilterOrder';
import {
  getVisibleFilterCategoryEntries,
  VisibleFilterValueEntry,
} from './filterRows';
import { useQuickFilters } from './QuickFiltersProvider';

const styles = StyleSheet.create({
  filtersSidebarContainer: {
    width: 400,
    borderRightWidth: 1,
    borderRightColor: colors['color-gray-100'],
  },
  actionBar: {
    backgroundColor: 'white',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  childCardStyle: {
    borderWidth: 0,
    borderBottomWidth: 1,
    borderRadius: 0,
    width: '100%',
  },
  fullWidth: {
    width: '100%',
  },
  filterRowItem: {
    paddingVertical: 6,
    width: '100%',
  },
  filterValueWrapper: {
    minWidth: 0,
    flexShrink: 1,
  },
  filterValueTextContainer: {
    minWidth: 0,
    flexShrink: 1,
  },
  filterValueText: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  filterCountText: {
    flexShrink: 0,
  },
  aiFilterCard: {
    borderWidth: 0,
    borderColor: colors['color-primary-300'],
    borderBottomWidth: 1,
    borderRadius: 0,
    backgroundColor: colors['color-ui2-brand-50'],
    marginVertical: 0,
    width: '100%',
  },
  lastAiFilterCard: {
    borderBottomWidth: 0,
  },
  aiFilterContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  aiFilterGroup: {
    borderWidth: 1,
    borderColor: colors['color-primary-300'],
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: colors['color-ui2-brand-50'],
    marginBottom: 12,
  },
  staticFilterCard: {
    minHeight: 'fit-content',
    borderRadius: 8,
    overflow: 'hidden',
    width: '100%',
  },
  viewAllFilterRow: {
    height: 56,
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  viewAllButton: {
    margin: 0,
  },
})

const MAX_VISIBLE_STANDARD_FILTERS = 5;

function getVisibleStandardFilterCategories<T>(
  categories: T[],
  aiFiltersEnabled: boolean,
  showAllStandardFilters: boolean,
): T[] {
  if (!aiFiltersEnabled || showAllStandardFilters) {
    return categories;
  }

  return categories.slice(0, MAX_VISIBLE_STANDARD_FILTERS);
}

const shouldShowStandardFiltersToggle = (
  aiFiltersEnabled: boolean,
  categoryCount: number,
): boolean => aiFiltersEnabled && categoryCount > MAX_VISIBLE_STANDARD_FILTERS;

const getAiFilterCardStyle = (isLast: boolean) => [
  styles.aiFilterCard,
  isLast && styles.lastAiFilterCard,
];

type FilterPressableProps = DNABoxProps & PressableProps;

const PressableDNABox = DNABox as React.ComponentType<FilterPressableProps>;

interface CustomHeaderProps {
  count: number
  title: string
  allFilters?: boolean
}

interface FilterValueRowProps {
  active: boolean
  count: number
  fieldId: string
  fieldValue: string
  textProps: DNATextProps
  valueId: string
  onToggleFilter: (fieldId: string, valueId: string) => void
}

export const FilterValueRow: React.FC<FilterValueRowProps> = ({
  active,
  count,
  fieldId,
  fieldValue,
  textProps,
  valueId,
  onToggleFilter,
}) => {
  const handlePress = () => {
    onToggleFilter(fieldId, valueId);
  };

  return (
    <PressableDNABox
      accessibilityRole="checkbox"
      accessibilityState={{ checked: active }}
      appearance="row"
      as={Pressable}
      spacing="md"
      childFill={1}
      childStyle={[1, styles.filterValueWrapper]}
      alignY="center"
      onPress={handlePress}
      style={styles.filterRowItem}
      testID="filter-value-row"
    >
      <DNABox pointerEvents="none">
        <DNACheckbox
          checked={active}
          onChange={handlePress}
        />
      </DNABox>
      <DNABox
        fill
        appearance="row"
        alignY="center"
        spacing="xs"
        childFill={0}
        childStyle={[0, styles.filterValueWrapper]}
        style={styles.filterValueTextContainer}
        testID="filter-value-text-container"
      >
        <DNAText
          testID="filter-name-text"
          {...textProps}
          numberOfLines={1}
          style={[textProps.style, styles.filterValueText]}
        >
          {fieldValue}
        </DNAText>
        <DNAText
          testID="filter-count-text"
          numberOfLines={1}
          style={styles.filterCountText}
        >
          ({count})
        </DNAText>
      </DNABox>
    </PressableDNABox>
  );
};

const CustomHeader: React.FC<CustomHeaderProps> = (
  { count, title, allFilters },
) => {
  const route = useCurrentPage({ exact: false });
  const isPublisher = route?.configOptions?.modules?.includes('publisher');

  return (
    <DNABox spacing="sm" alignY="center">
      <DNAText
        numberOfLines={1}
        testID={`header-${title?.toLocaleLowerCase().replaceAll(' ', '-')}`}
        style={{ maxWidth: 300 }}
        bold
      >{title}</DNAText>
      {count > 0 &&
        <DNAChip.Beacon
          appearance={allFilters ? 'filled' : 'outline'}
          status={isPublisher ? 'secondary' : 'primary'}
          children={`${count}`}
        />
      }
    </DNABox>
  )
}

const FilterCollapsibleCard: React.FC<PropsWithChildren<
CollapsibleCardProps
& { disabled?: boolean }
>> = (props) => {
  const { disabled, children } = props

  if (disabled) {
    return (
      <DNACard
        appearance="outline"
        style={[styles.staticFilterCard, props.cardStyle]}
      >
        {children}
      </DNACard>
    )
  }

  return (<CollapsibleCard {...props}>{children}</CollapsibleCard>)
}

interface AiFilterCardProps {
  fieldName: string
  isLast: boolean
  visibleValues: VisibleFilterValueEntry[]
  selectedCount?: number
  textProps: DNATextProps
  onToggleFilter: (fieldId: string, valueId: string) => void
}

const AiFilterCard: React.FC<AiFilterCardProps> = ({
  fieldName,
  isLast,
  visibleValues,
  selectedCount = 0,
  textProps,
  onToggleFilter,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <FilterCollapsibleCard
      key={fieldName}
      headerTitle={(
        <DNABox spacing="sm" alignY="center">
          <DNAIcon.Styled name="sparkle" color={colors['color-primary-600']} width={18} height={18} />
          <CustomHeader title={fieldName} count={selectedCount} />
        </DNABox>
      )}
      isCollapsed={isCollapsed}
      onToggleChanged={setIsCollapsed}
      collapseRight
      cardStyle={getAiFilterCardStyle(isLast)}
      contentPadding="none"
    >
      <DNABox spacing="sm" appearance="col" style={styles.aiFilterContent}>
        {
          visibleValues.map(([_, {
            fieldId,
            valueId,
            active,
            fieldValue,
          }, count]) => (
            <FilterValueRow
              key={`${fieldId}-${valueId}`}
              active={active}
              count={count}
              fieldId={fieldId}
              fieldValue={fieldValue}
              textProps={textProps}
              valueId={valueId}
              onToggleFilter={onToggleFilter}
            />
          ))
        }
      </DNABox>
    </FilterCollapsibleCard>
  );
}

export const useFilterMap = () => {
  const {
    activeFilters,
    filterEntries,
    filterCategoryOrder,
    restrictToConfiguredFilterCategories,
  } = useDNADocumentFilters();
  const currentUserORM = useCurrentUserORM();
  const filterMap = useMemo(() => {
    const mappedFilters = filterEntries
      .reduce<FilterMap>((acc, entry) => {
        const { fieldId, valueId } = entry;
        if (!acc[fieldId]) {
          acc[fieldId] = {
            fieldName: entry.fieldName,
            values: {},
          };
        }

        acc[fieldId].values[valueId] = {
          fieldId,
          valueId,
          fieldName: entry.fieldName,
          fieldValue: entry.fieldValue,
          active: activeFilters.find((f) => f.fieldId === fieldId && f.valueId === valueId)?.active ?? false,
          isAiGeneratedLabel: entry.isAiGeneratedLabel,
        };

        return acc;
      }, {})

    currentUserORM?.model.lockedFiltersCustomValues.forEach((lockedFilter) => {
      if (mappedFilters[lockedFilter.fieldId]) {
        Object.keys(mappedFilters[lockedFilter.fieldId].values).forEach((valueId) => {
          if (!lockedFilter.values.includes(valueId)) {
            delete mappedFilters[lockedFilter.fieldId].values[valueId];
          }
        });
      }
    });

    return mappedFilters;
  }, [activeFilters, currentUserORM, filterEntries])

  let totalSelected = 0;

  const regularFilterCategoryOrder = filterCategoryOrder.filter((category) => !category.isAiGenerated);
  const aiGeneratedFilterCategoryOrder = filterCategoryOrder.filter((category) => category.isAiGenerated);

  const categoryEntries = regularFilterCategoryOrder
    .map((category) => [category.fieldId, filterMap[category.fieldId]] as const)
    .filter(([, field]) => !!field && Object.keys(field.values).length > 0);
  const aiCategoryEntries = aiGeneratedFilterCategoryOrder
    .map((category) => [
      category.fieldId,
      filterMap[category.fieldId] || {
        fieldName: category.fieldName,
        values: {},
      },
    ] as const);

  const remainingCategories = Object.entries(filterMap)
    .filter(([fieldId, field]) => {
      return !filterCategoryOrder.some((category) => category.fieldId === fieldId) &&
        Object.keys(field.values).length > 0;
    });

  const categories = restrictToConfiguredFilterCategories
    ? categoryEntries
    : [...categoryEntries, ...remainingCategories];
  const allCategories = [...categories, ...aiCategoryEntries];
  const filterSelectedCount = allCategories.reduce<Record<string, number>>((prev, curr) => {
    const [, field] = curr;
    Object.entries(field.values).forEach(([_, fieldEntry]) => {
      prev[fieldEntry.fieldName] = prev[fieldEntry.fieldName] ?? 0;
      if (fieldEntry.active) {
        prev[fieldEntry.fieldName] += 1
        totalSelected += 1;
      }
    })
    return prev;
  }, {})

  return {
    filterMap,
    filterSelectedCount,
    totalSelected,
    categories,
    aiCategories: aiCategoryEntries,
  };
}

interface FiltersSidebarProps {
  textValue?: string
  titleSearchText?: string
  setVisible: (visible: boolean) => void
  disableQuickFilters?: boolean
  documents?: DocumentORM[]
  style?: StyleProp<ViewStyle>
}

const FiltersSidebarContent: React.FC<FiltersSidebarProps & { documents: DocumentORM[] }> = React.memo((props) => {
  const { setVisible, disableQuickFilters } = props;
  const { deviceMode } = useAppSettings();
  const {
    quickFilters,
    selectedPath,
    visibleRows,
    handleToggleFilter,
    handleToggleFilterValue,
    handleClear: handleClearQuickFilters,
  } = useQuickFilters();
  const {
    activeFilters,
    toggleFilter,
    clearFilters,
    resetToDefaultFilters,
    unpublishedToggle,
  } = useDNADocumentFilters();
  const userTenant = useUserTenant();
  const enableAiDocumentLabels = useFeatureFlags('BEAC_8172_ai_document_labels');
  const route = useCurrentPage({ exact: false });
  const isPublisher = route?.configOptions?.modules?.includes('publisher');
  const allDocuments = props.documents;
  const { filterSelectedCount, totalSelected, categories, aiCategories } = useFilterMap();
  const [showAllStandardFilters, setShowAllStandardFilters] = useState(false);

  const { disableQuickFilters: disableQuickFiltersOnRoute } = { ...route?.configOptions?.[deviceMode] }
  const shouldDisableQuickFilters = disableQuickFilters || disableQuickFiltersOnRoute;

  const getDocumentsCountForField = useCallback((fieldId: string) => {
    return countDocumentCustomValues(
      allDocuments,
      isPublisher,
      userTenant,
      activeFilters,
      fieldId,
      unpublishedToggle,
    );
  }, [allDocuments, isPublisher, userTenant, activeFilters, unpublishedToggle]);

  const documentCountsByField = useMemo(() => {
    const counts: Record<string, DocumentCount> = {};

    visibleRows.forEach(field => {
      counts[field.id] = getDocumentsCountForField(field.id);
    });

    categories.forEach(([_, { values }]) => {
      const firstValue = Object.values(values)[0];
      const fieldId = firstValue?.fieldId;
      if (fieldId && !counts[fieldId]) {
        counts[fieldId] = getDocumentsCountForField(fieldId);
      }
    });

    aiCategories.forEach(([_, { values }]) => {
      const firstValue = Object.values(values)[0];
      const fieldId = firstValue?.fieldId;
      if (fieldId && !counts[fieldId]) {
        counts[fieldId] = getDocumentsCountForField(fieldId);
      }
    });

    return counts;
  }, [getDocumentsCountForField, visibleRows, categories, aiCategories]);

  const handleToggleFilterCallback = useCallback((fieldId: string, valueId: string) => {
    toggleFilter(fieldId, valueId);
  }, [toggleFilter]);

  const textProps: DNATextProps = {
    numberOfLines: 3,
    selectable: false,
    style: { flex: 1 },
  }

  const pathLabels = (
    <DNABox>
      <CollapsibleCard
        headerTitle="Find Content By"
        collapseRight
        cardStyle={styles.childCardStyle}
      >
        <DNABox appearance="col" spacing="sm" style={{ width: 300 }} fill>
          {quickFilters.map((filter, index) => {
            const isSelected = selectedPath === index;
            return (
              <DNABox appearance="row" spacing="md" alignY="center" style={styles.filterRowItem}>
                <DNACheckbox
                  key={filter.id}
                  checked={isSelected}
                  onChange={() => handleToggleFilter(filter.id, isSelected, index)}
                  testID="quickfilter-path"
                />
                <DNAText {...textProps}>
                  {filter.label}
                </DNAText>
              </DNABox>
            )
          })}
        </DNABox>
      </CollapsibleCard>
    </DNABox>
  )

  const filterRows = (
    // for some reason, the component gets wonky without the spacing property, childStyle is added to override the margin added by the property
    <DNABox appearance="col" spacing="sm" childStyle={{ marginBottom: 0 }}>
      {visibleRows.map((field) => {
        const documentsCount = documentCountsByField[field.id] || {};
        const visibleFieldValueDefinitions = field.fieldValueDefinitions
          .filter((value) => (documentsCount[field.id]?.[value.id] ?? 0) > 0);

        if (!visibleFieldValueDefinitions.length) {
          return null;
        }

        return (
          <CollapsibleCard
            key={field.id}
            headerTitle={field.fieldLabel}
            collapseRight
            cardStyle={styles.childCardStyle}
          >
            <DNABox appearance="col" spacing="sm">
              {visibleFieldValueDefinitions.map((value) => {
                const isSelected = !!activeFilters.find((v) => v.valueId === value.id);
                return (
                  <FilterValueRow
                    key={value.id}
                    active={isSelected}
                    count={documentsCount[field.id]?.[value.id] ?? 0}
                    fieldId={field.id}
                    fieldValue={value.label ?? value.value}
                    textProps={textProps}
                    valueId={value.id}
                    onToggleFilter={() => handleToggleFilterValue(field, value, isSelected)}
                  />
                )
              })}
            </DNABox>
          </CollapsibleCard>
        )
      })}
    </DNABox>
  )

  const handleClose = () => {
    setVisible(false);
  }

  const handleReset = () => {
    resetToDefaultFilters();
  }

  const handleClear = () => {
    // clear regular filters
    clearFilters();
    // clear quick filters
    if (!shouldDisableQuickFilters) handleClearQuickFilters();
  }

  const visibleAiCategories = getVisibleFilterCategoryEntries(aiCategories, documentCountsByField);
  const sortedAiCategories = sortAiCategoryEntries(visibleAiCategories);
  const aiFilterRows = sortedAiCategories
    .map(([_, { fieldName }, visibleValues], index) => {
      return (
        <AiFilterCard
          key={fieldName}
          fieldName={fieldName}
          isLast={index === sortedAiCategories.length - 1}
          visibleValues={sortAiFilterValueEntries(visibleValues)}
          selectedCount={filterSelectedCount[fieldName]}
          textProps={textProps}
          onToggleFilter={handleToggleFilterCallback}
        />
      );
    });
  const aiFiltersSection = aiFilterRows.length > 0 ? (
    <DNABox
      appearance="col"
      spacing="xs"
      childStyle={{ marginBottom: 0, width: '100%' }}
      style={[styles.aiFilterGroup, styles.fullWidth]}
    >
      {aiFilterRows}
    </DNABox>
  ) : null;
  const visibleCategories = getVisibleFilterCategoryEntries(categories, documentCountsByField, VIDEO_TYPES_ENUM);
  const visibleStandardCategories = getVisibleStandardFilterCategories(
    visibleCategories,
    enableAiDocumentLabels,
    showAllStandardFilters,
  );
  const showStandardFiltersToggle = shouldShowStandardFiltersToggle(
    enableAiDocumentLabels,
    visibleCategories.length,
  );
  const standardFiltersSection = (
    <DNABox
      appearance="col"
      spacing="sm"
      childStyle={{ marginBottom: 0, width: '100%' }}
      style={styles.fullWidth}
    >
      {visibleStandardCategories.map(([_, { fieldName }, visibleValues]) => {
        return (
          <FilterCollapsibleCard
            key={fieldName}
            headerTitle={<CustomHeader title={fieldName} count={filterSelectedCount[fieldName]} />}
            numberOfTitleLines={3}
            collapsedDefault
            collapseRight
            cardStyle={styles.childCardStyle}
          >
            <DNABox spacing="sm" appearance="col">
              {
                visibleValues.map(([_, { fieldId, valueId, active, fieldValue }, count]) => (
                  <FilterValueRow
                    key={`${fieldId}-${valueId}`}
                    active={active}
                    count={count}
                    fieldId={fieldId}
                    fieldValue={fieldValue}
                    textProps={textProps}
                    valueId={valueId}
                    onToggleFilter={handleToggleFilterCallback}
                  />
                ))
              }
            </DNABox>
          </FilterCollapsibleCard>
        );
      })}
      <Iffy is={showStandardFiltersToggle}>
        <DNABox appearance="col" alignX="stretch" alignY="center" style={styles.viewAllFilterRow}>
          <DNAButton
            appearance="filled"
            status="secondary"
            size="lg"
            stretch
            style={styles.viewAllButton}
            onPress={() => setShowAllStandardFilters((showAll) => !showAll)}
          >
            {showAllStandardFilters ? 'View less' : 'View all'}
          </DNAButton>
        </DNABox>
      </Iffy>
    </DNABox>
  );
  const allFiltersContent = standardFiltersSection;

  return (
    <DNABox style={[styles.filtersSidebarContainer, props.style]} appearance="col">
      <Iffy is={deviceMode === DeviceMode.tablet}>
        <DNABox spacing="sm" style={styles.actionBar}>
          <DNAButton onPress={handleClose} children="Close" />
          <DNAButton appearance="outline" onPress={handleReset} children="Reset to default"/>
          <DNAButton testID="quickfilter-clear-button" appearance="outline" onPress={handleClear} children="Clear"/>
        </DNABox>
      </Iffy>
      <DNABox fill spacing="md" appearance="col">
        <Iffy is={quickFilters.length && !isPublisher && !shouldDisableQuickFilters}>
          <CollapsibleCard
            headerTitle="QUICK FILTERS"
            variant="filters"
            collapseRight
            contentPadding="none"
          >
            <DNABox appearance="col">
              {pathLabels}
              {filterRows}
            </DNABox>
          </CollapsibleCard>
        </Iffy>
        <DNABox appearance="col" spacing="md" style={styles.fullWidth}>
          <FilterCollapsibleCard
            headerTitle={<CustomHeader title="ALL FILTERS" count={totalSelected} allFilters/>}
            variant="filters"
            collapseRight
            contentPadding="none"
            disabled={!!isPublisher}
          >
            {allFiltersContent}
          </FilterCollapsibleCard>
          {aiFiltersSection}
        </DNABox>
      </DNABox>
    </DNABox>
  )
})

const FiltersSidebarWithLibrary: React.FC<FiltersSidebarProps> = (props) => {
  const route = useCurrentPage({ exact: false });
  const isPublisher = route?.configOptions?.modules?.includes('publisher');
  const { allDocuments } = useDNALibrary(props.textValue, isPublisher, props.titleSearchText);
  return <FiltersSidebarContent {...props} documents={allDocuments} />;
};

const FiltersSidebar: React.FC<FiltersSidebarProps> = (props) => props.documents
  ? <FiltersSidebarContent {...props} documents={props.documents} />
  : <FiltersSidebarWithLibrary {...props} />;

export default FiltersSidebar;
